// src/components/events/EventDetail.jsx
import React from 'react';
import Badge from '../common/Badge';
import { eventSourceLabel } from '../../utils/eventSource';

const EventDetail = ({ event, onClose, siteName }) => {
  if (!event) return null;

  // Extract security-relevant information
  const details = event.details || {};
  const triggeringEvent = details.triggering_event_details || {};
  const detectedPatterns = triggeringEvent.detected_patterns || [];

  // Format timestamp - Convert from UTC to CDT
  const formatTimestamp = (timestamp) => {
    // Add 'Z' to tell JavaScript this is UTC time
    const date = new Date(timestamp + 'Z');
    return date.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <div 
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4" 
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Security Event Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Event ID: {event.event_id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1">
          <div className="border-t border-gray-200">
            <dl>
              {/* Severity */}
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Severity</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <Badge type={event.severity} />
                </dd>
              </div>

              {/* Event Type */}
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Event Type</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{event.event_type}</dd>
              </div>

              {/* Timestamp */}
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Timestamp</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{formatTimestamp(event.timestamp)}</dd>
              </div>

              {/* Edge Agent */}
              {event.celerway_edge_node_id && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Edge Agent</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 bg-blue-100 text-blue-500 rounded-full flex items-center justify-center">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                        </svg>
                      </div>
                      <span className="ml-3">{siteName || event.celerway_edge_node_id}</span>
                    </div>
                  </dd>
                </div>
              )}

              {/* Description */}
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{event.description}</dd>
              </div>

              {/* Status */}
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    event.status === 'New' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {event.status}
                  </span>
                </dd>
              </div>

              {/* Threat Patterns - Warning Section */}
              {detectedPatterns.length > 0 && (
                <div className="bg-red-50 px-4 py-5 sm:px-6 border-l-4 border-red-400">
                  <dt className="text-sm font-medium text-red-800 mb-3">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Detected Threat Patterns ({detectedPatterns.length})
                    </div>
                  </dt>
                  <dd className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {detectedPatterns.map((pattern, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                        >
                          {pattern}
                        </span>
                      ))}
                    </div>
                  </dd>
                </div>
              )}
            </dl>
          </div>



{/* OT Protocol Metadata */}
{details && (details.modbus_metadata || details.opcua_metadata) && (
  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
    <h4 className="text-sm font-medium text-gray-900 mb-4">
      OT Protocol Metadata
    </h4>

    {/* Modbus Metadata */}
    {details.modbus_metadata && (
      <div className="mb-6">
        <h5 className="text-sm font-medium text-gray-800 mb-2">
          Modbus Device Identification
        </h5>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <div>
            <dt className="text-sm text-gray-500">Vendor</dt>
            <dd className="text-sm text-gray-900">
              {details.modbus_metadata.vendor || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Product Code</dt>
            <dd className="text-sm text-gray-900">
              {details.modbus_metadata.product_code || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Revision</dt>
            <dd className="text-sm text-gray-900">
              {details.modbus_metadata.revision || "—"}
            </dd>
          </div>
        </dl>
      </div>
    )}

    {/* OPC-UA Metadata */}
    {details.opcua_metadata && (
      <div>
        <h5 className="text-sm font-medium text-gray-800 mb-2">
          OPC-UA Endpoint Information
        </h5>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <div>
            <dt className="text-sm text-gray-500">Endpoint URL</dt>
            <dd className="text-sm text-gray-900">
              {details.opcua_metadata.endpoint_url}
            </dd>
          </div>

          <div>
            <dt className="text-sm text-gray-500">Application Name</dt>
            <dd className="text-sm text-gray-900">
              {details.opcua_metadata.application_name}
            </dd>
          </div>

          <div>
            <dt className="text-sm text-gray-500">Application URI</dt>
            <dd className="text-sm text-gray-900">
              {details.opcua_metadata.application_uri}
            </dd>
          </div>

          <div>
            <dt className="text-sm text-gray-500">Product URI</dt>
            <dd className="text-sm text-gray-900">
              {details.opcua_metadata.product_uri}
            </dd>
          </div>

          <div>
            <dt className="text-sm text-gray-500">Security Policies</dt>
            <dd className="text-sm text-gray-900">
              {(details.opcua_metadata.security_policies || []).join(", ")}
            </dd>
          </div>

          <div>
            <dt className="text-sm text-gray-500">Security Modes</dt>
            <dd className="text-sm text-gray-900">
              {(details.opcua_metadata.security_modes || []).join(", ")}
            </dd>
          </div>

          <div className="sm:col-span-2">
            <dt className="text-sm text-gray-500">Discovered Endpoints</dt>
            <dd className="text-sm text-gray-900">
              {(details.opcua_metadata.endpoints || []).join(", ")}
            </dd>
          </div>
        </dl>
      </div>
    )}
  </div>
)}

{/* Risk Assessment */}
{details && details.risk_level && (
  <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
    <h4 className="text-sm font-medium text-gray-900 mb-4">Risk Assessment</h4>
    <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
      <div>
        <dt className="text-sm text-gray-500">Risk Level</dt>
        <dd className="text-sm text-gray-900">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            details.risk_level === "High" 
              ? "bg-red-100 text-red-800"
              : details.risk_level === "Medium"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-green-100 text-green-800"
          }`}>
            {details.risk_level}
          </span>
        </dd>
      </div>

      <div>
        <dt className="text-sm text-gray-500">Risk Score</dt>
        <dd className="text-sm text-gray-900">
          {details.risk_score}
        </dd>
      </div>
    </dl>
  </div>
)}

          {/* Network Traffic Section */}
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Network Traffic</h4>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Source IP</dt>
                <dd className="mt-1 text-sm text-gray-900">{eventSourceLabel(event)}</dd>
                {triggeringEvent.source_port && (
                  <dd className="mt-1 text-xs text-gray-500">Port: {triggeringEvent.source_port}</dd>
                )}
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Destination IP</dt>
                <dd className="mt-1 text-sm text-gray-900">{event.destination_ip || 'N/A'}</dd>
                {triggeringEvent.dest_port && (
                  <dd className="mt-1 text-xs text-gray-500">Port: {triggeringEvent.dest_port}</dd>
                )}
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Protocol</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {event.protocol || 'N/A'}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Service</dt>
                <dd className="mt-1 text-sm text-gray-900">{triggeringEvent.service_hint || 'Unknown'}</dd>
              </div>
            </dl>

            {/* Traffic Metadata */}
            {triggeringEvent.packet_size && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h5 className="text-sm font-medium text-gray-900 mb-3">Packet Details</h5>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Packet Size</dt>
                    <dd className="mt-1 text-sm text-gray-900">{triggeringEvent.packet_size} bytes</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">TCP Flags</dt>
                    <dd className="mt-1 text-sm text-gray-900">{triggeringEvent.tcp_flags || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Event Source</dt>
                    <dd className="mt-1 text-sm text-gray-900">{triggeringEvent.event_source?.replace(/_/g, ' ') || 'N/A'}</dd>
                  </div>
                </dl>
              </div>
            )}
          </div>

          {/* Policy Information Section */}
          {details.policy_name && (
            <div className="border-t border-gray-200">
              <div className="px-4 py-5 sm:px-6">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Policy Match</h4>
                <dl>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Policy Name</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{details.policy_name}</dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Rule Name</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{details.rule_name}</dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Action Taken</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {details.rule_action}
                      </span>
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Rule Condition</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">{details.rule_condition}</code>
                    </dd>
                  </div>
                  {details.rule_description && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Description</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{details.rule_description}</dd>
                    </div>
                  )}
                </dl>

                {/* Policy Scope */}
                {details.policy_scope_identifiers && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Policy Scope</h5>
                    <dl className="space-y-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Scope Type</dt>
                        <dd className="mt-1 text-sm text-gray-900">{details.policy_scope_type}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 mb-2">Applied To</dt>
                        <dd className="flex flex-wrap gap-2">
                          {details.policy_scope_identifiers.map((identifier, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {identifier}
                            </span>
                          ))}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Raw Event Data */}
          {details.triggering_event_description && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Raw Event Data</h4>
              <div className="bg-gray-50 rounded-md p-4">
                <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words">{details.triggering_event_description}</pre>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-4 py-4 sm:px-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Mark as Reviewed
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
